<script>
  /* global Global, DEV_ENV */
  import Header from '../../common/Header.svelte';
  import Footer from '../../common/Footer.svelte';
  import Loading from '../../common/Loading.svelte';
  import Error from '../../common/Error.svelte';
  import Title from '../../common/Title.svelte';
  import Input from '../../common/Input.svelte';
  import Textarea from '../../common/Textarea.svelte';
  import PhotoDragAndDrop from '../../common/PhotoDragAndDrop.svelte';
  import Button from '../../common/Button.svelte';
  import MessageBox from '../../common/MessageBox.svelte';
  import ConfirmModal from '../../common/ConfirmModal.svelte';
  import Common from '../../js/common.js';
  import Validation from '../../js/validation.js';
  import ALL_TEAM_FIELDS from '../../js/fields/allTeamFields.js';

  export let params;

  let lang;

  let user = JSON.parse(window.localStorage.getItem('user'));
  if (DEV_ENV) user = { firstName: 'Jan', lastName: 'Novák', roles: ['ROLE_ADMIN'] };
  if (!user.roles.includes('ROLE_ADMIN')) window.location.href = window.localStorage.getItem('previousPage') || './';

  let team;
  let photo;
  let action = 'edit';
  let code;
  let timestamp;

  let loading = true;
  let error = false;

  if (params.team === 'add') {
    team = {
      id: undefined,
      code: undefined,
      wbscId: undefined,
      shortcut: undefined,
      name: { cs: undefined, en: undefined },
      group: undefined,
      founded: undefined,
      web: undefined,
      wbscEurope: undefined,
      facebook: undefined,
      instagram: undefined,
      twitter: undefined,
      youtube: undefined,
      standings: [undefined, undefined, undefined, undefined],
      content: { cs: undefined, en: undefined },
      slot: undefined,
      photo: 0,
      timestamp: { date: undefined, time: undefined },
    };

    action = 'add';

    loading = false;

    if (!DEV_ENV) {
      (async () => {
        // eslint-disable-next-line no-undef
        const response = await fetch(`${Common.BACKEND_URL}/user/logged`);

        if (response.ok) {
          const data = await response.json();

          user = data;
          window.localStorage.setItem('user', JSON.stringify(data));
        } else {
          user = undefined;
          window.localStorage.removeItem('user');

          back();
        }
      })();
    }
  } else {
    (async () => {
      // eslint-disable-next-line no-undef
      const response = await fetch(`${Common.BACKEND_URL}/teams/get/${params.team}`);

      if (response.ok) {
        const data = await response.json();

        team = data.team;
        user = data.user;

        if (user) {
          window.localStorage.setItem('user', JSON.stringify(data.user));
        } else {
          window.localStorage.removeItem('user');

          back();
        }

        Object.keys(team).forEach(key => {
          if (team[key] === null) {
            team[key] = undefined;
          } else if (typeof team[key] === 'object') {
            Object.keys(team[key]).forEach(nestedKey => {
              if (team[key][nestedKey] === null) team[key][nestedKey] = undefined;
            });
          }
        });

        code = team.code;
        timestamp = team.timestamp;

        if (timestamp > Common.getDbDate() + Common.getDbTime()) {
          team.timestamp = {
            date: Common.convertDbDate(timestamp),
            time: Common.convertDbTime(timestamp),
          };
        }
      } else {
        if (response.status === Common.NOT_FOUND || response.status === Common.UNAUTHORIZED) window.location.href = './';
        else error = true;
      }

      loading = false;
    })();
  }

  $: if (!loading && !error) {
    team.code = Common.toCode(team.name.en).replaceAll(/ /g, '-');
  }

  let messageBox;
  let display = 'buttons';
  let errors = [];

  function addOrEditTeam (validate = true) {
    display = 'loading';
    errors = [];

    if (action === 'add') {
      messageBox = {
        cs: { color: 'blue', message: i18n.addingInProgress.cs },
        en: { color: 'blue', message: i18n.addingInProgress.en },
      };
    } else {
      messageBox = {
        cs: { color: 'blue', message: i18n.savingInProgress.cs },
        en: { color: 'blue', message: i18n.savingInProgress.en },
      };
    }

    try {
      // eslint-disable-next-line no-undef
      const formData = new FormData();

      const data = Validation.convertAndCheck(ALL_TEAM_FIELDS, validate,
        {
          ...team,
          photo: photo ? team.photo + 1 : team.photo,
        },
      );

      formData.append('data', JSON.stringify(data));
      if (photo) formData.append('photo', photo);

      if (action === 'add') {
        (async () => {
          // eslint-disable-next-line no-undef
          const response = await fetch(`${Common.BACKEND_URL}/admin/teams/add`, {
            method: 'POST',
            body: formData,
          });

          if (response.ok) {
            const data = await response.text();

            messageBox = {
              cs: { color: 'green', message: `Tým "[${Common.addZeros(data)}] ${team.code}" byl úspěšně přidán.` },
              en: { color: 'green', message: `Team "[${Common.addZeros(data)}] ${team.code}" has been successfully added.` },
            };

            display = 'saved';
          } else {
            messageBox = {
              cs: { color: 'red', message: i18n.unexpectedError.cs },
              en: { color: 'red', message: i18n.unexpectedError.en },
            };

            display = 'error';
          }
        })();
      } else {
        (async () => {
          // eslint-disable-next-line no-undef
          const response = await fetch(`${Common.BACKEND_URL}/admin/teams/edit/${team.id}`, {
            method: 'POST',
            body: formData,
          });

          if (response.ok) {
            const data = await response.text();

            messageBox = {
              cs: { color: 'green', message: `Úpravy týmu "[${Common.addZeros(data)}] ${team.code}" byly úspěšně uloženy.` },
              en: { color: 'green', message: `Team "[${Common.addZeros(data)}] ${team.code}" changes have been successfully saved.` },
            };

            display = 'saved';
          } else {
            messageBox = {
              cs: { color: 'red', message: i18n.unexpectedError.cs },
              en: { color: 'red', message: i18n.unexpectedError.en },
            };

            display = 'error';
          }
        })();
      }
    } catch (error) {
      errors = error.errors;

      messageBox = {
        cs: { color: 'red', errors: errors.map(error => error.message.cs) },
        en: { color: 'red', errors: errors.map(error => error.message.en) },
      };

      display = 'error';
    }
  }

  function back () {
    window.location.href = window.localStorage.getItem('previousPage') || './';
  }

  async function translate (key) {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/translate`, {
      method: 'POST',
      body: team[key].cs,
    });

    if (response.ok) {
      team[key].en = await response.text();
    }
  }

  let displayConfirmModal = false;
  let confirmModalMessage;

  $: if (!loading && !error) {
    confirmModalMessage = {
      cs: `Opravdu chcete zahodit ${action === 'edit' ? 'úpravy týmu' : 'tým'}`,
      en: `Do you really want to discard ${action === 'edit' ? 'changes of team' : 'team'}`,
    };

    if (team.id && (code || team.code)) {
      confirmModalMessage.cs += ` "[${Common.addZeros(team.id)}] ${code || team.code}"?`;
      confirmModalMessage.en += ` "[${Common.addZeros(team.id)}] ${code || team.code}"?`;
    } else if (team.id) {
      confirmModalMessage.cs += ` "[${Common.addZeros(team.id)}]"?`;
      confirmModalMessage.en += ` "[${Common.addZeros(team.id)}]"?`;
    } else if (code || team.code) {
      confirmModalMessage.cs += ` "${code || team.code}"?`;
      confirmModalMessage.en += ` "${code || team.code}"?`;
    } else {
      confirmModalMessage.cs += '?';
      confirmModalMessage.en += '?';
    }
  }

  const i18n = {
    pageTitle: {
      cs: `Admin | ${Global.project.name.cs}`,
      en: `Admin | ${Global.project.name.en}`,
    },
    shortcut: {
      cs: 'Zkratka',
      en: 'Shortcut',
    },
    name: {
      cs: 'Název',
      en: 'Name',
    },
    group: {
      cs: 'Skupina',
      en: 'Group',
    },
    founded: {
      cs: 'Založeno',
      en: 'Founded',
    },
    standings: {
      cs: 'Umístění',
      en: 'Standings',
    },
    content: {
      cs: 'Obsah',
      en: 'Content',
    },
    plannedPublicationDate: {
      cs: 'Datum plánovaného zveřejnění (volitelné)',
      en: 'Date of planned publication (optional)',
    },
    plannedPublicationTime: {
      cs: 'Čas plánovaného zveřejnění (volitelné)',
      en: 'Time of planned publication (optional)',
    },
    yearFormat: {
      cs: 'RRRR',
      en: 'YYYY',
    },
    dateFormat: {
      cs: 'DD.MM.RRRR',
      en: 'DD.MM.YYYY',
    },
    automaticallyTranslate: {
      cs: 'Automaticky přeložit',
      en: 'Automatically translate',
    },
    translateInProgress: {
      cs: 'Probíhá překlad...',
      en: 'Translate in progress...',
    },
    addTeam: {
      cs: 'Přidat tým',
      en: 'Add team',
    },
    saveChanges: {
      cs: 'Uložit úpravy',
      en: 'Save changes',
    },
    discardTeam: {
      cs: 'Zahodit tým',
      en: 'Discard team',
    },
    discardChanges: {
      cs: 'Zahodit úpravy',
      en: 'Discard changes',
    },
    back: {
      cs: 'Zpět',
      en: 'Back',
    },
    addingInProgress: {
      cs: 'Probíhá přidávání týmu...',
      en: 'Team adding in progress...',
    },
    savingInProgress: {
      cs: 'Probíhá ukládání úprav...',
      en: 'Changes saving in progress...',
    },
    unexpectedError: {
      cs: '(Ne)očekávaná chyba. `E29`',
      en: '(Un)expected error. `E29`',
    },
    withoutValidation: {
      cs: 'bez validace',
      en: 'without validation',
    },
  };

  const standings = Global.teams.standings;
</script>

<svelte:head>
  <title> {i18n.pageTitle[lang]} </title>
</svelte:head>

<Header page='admin/teams/{params.team}' bind:lang bind:user/>

<Title/>

<div class='admin-teams-team'>
  <Loading bind:display={loading}/>
  <Error bind:display={error} bind:lang/>

  {#if !loading && !error}
    <div class='admin-teams-team-box'>
      <!-- CODE -->
      <Input bind:value={team.code} disabled={true}/>

      <div class='admin-teams-team-box-group'>
        <!-- WBSC ID -->
        <Input bind:value={team.wbscId} label='WBSC ID'
          error={errors.find(error => error.fieldName === 'wbscId')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>

        <!-- SHORTCUT -->
        <Input bind:value={team.shortcut} label={i18n.shortcut[lang]}
          error={errors.find(error => error.fieldName === 'shortcut')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>
      </div>

      <div class='admin-teams-team-box-group'>
        <!-- NAME CS -->
        <Input bind:value={team.name.cs} label='{i18n.name[lang]} [CS]'
          error={errors.find(error => error.fieldName === 'name.cs')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>

        <!-- NAME EN -->
        <Input bind:value={team.name.en} label='{i18n.name[lang]} [EN]'
          translate={i18n.automaticallyTranslate[lang]} onTranslateClick={() => { translate('name'); }}
          error={errors.find(error => error.fieldName === 'name.en')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>
      </div>

      <div class='admin-teams-team-box-group'>
        <!-- GROUP -->
        <Input bind:value={team.group} label={i18n.group[lang]}
          error={errors.find(error => error.fieldName === 'group')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>

        <!-- FOUNDED -->
        <Input bind:value={team.founded} label={i18n.founded[lang]} placeholder={i18n.yearFormat[lang]}
          error={errors.find(error => error.fieldName === 'founded')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>
      </div>

      <!-- WEB -->
      <Input bind:value={team.web} label='Web' prepend='https://'
        error={errors.find(error => error.fieldName === 'web')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- WBSC EUROPE -->
      <Input bind:value={team.wbscEurope} label='WBSC Europe' prepend='https://www.wbsceurope.org/en/members/profile/baseball/'
        error={errors.find(error => error.fieldName === 'wbscEurope')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- FACEBOOK -->
      <Input bind:value={team.facebook} label='Facebook' prepend='https://www.facebook.com/'
        error={errors.find(error => error.fieldName === 'facebook')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- INSTAGRAM -->
      <Input bind:value={team.instagram} label='Instagram' prepend='https://www.instagram.com/'
        error={errors.find(error => error.fieldName === 'instagram')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- TWITTER -->
      <Input bind:value={team.twitter} label='Twitter' prepend='https://www.twitter.com/'
        error={errors.find(error => error.fieldName === 'twitter')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- YOUTUBE -->
      <Input bind:value={team.youtube} label='YouTube' prepend='https://www.youtube.com/'
        error={errors.find(error => error.fieldName === 'youtube')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <div class='admin-teams-team-box-group'>
        <!-- STANDINGS [0] -->
        <Input bind:value={team.standings[0]} label={`${i18n.standings[lang]} (${standings[0].year})`}
          error={errors.find(error => error.fieldName === 'standings.0')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>

        <!-- STANDINGS [1] -->
        <Input bind:value={team.standings[1]} label={`${i18n.standings[lang]} (${standings[1].year})`}
          error={errors.find(error => error.fieldName === 'standings.1')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>

        <!-- STANDINGS [2] -->
        <Input bind:value={team.standings[2]} label={`${i18n.standings[lang]} (${standings[2].year})`}
          error={errors.find(error => error.fieldName === 'standings.2')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>

        <!-- STANDINGS [3] -->
        <Input bind:value={team.standings[3]} label={`${i18n.standings[lang]} (${standings[3].year})`}
          error={errors.find(error => error.fieldName === 'standings.3')?.message[lang]}
          disabled={['loading', 'saved'].includes(display)}/>
      </div>

      <!-- CONTENT CS -->
      <Textarea bind:value={team.content.cs} label='{i18n.content[lang]} [CS]'
        error={errors.find(error => error.fieldName === 'content.cs')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)} actions={true}/>

      <!-- CONTENT EN -->
      <Textarea bind:value={team.content.en} label='{i18n.content[lang]} [EN]'
        translate={i18n.automaticallyTranslate[lang]} onTranslateClick={() => { translate('content'); }}
        error={errors.find(error => error.fieldName === 'content.en')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)} actions={true}/>

      <!-- SLOT -->
      <Input bind:value={team.slot} label='Slot'
        error={errors.find(error => error.fieldName === 'slot')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- PHOTO -->
      <PhotoDragAndDrop bind:value={photo} bind:lang disabled={['loading', 'saved'].includes(display)}
        preview={team.photo ? `img/team/${code}-${team.photo}.jpg` : false}/>

      {#if action === 'add' || timestamp > Common.getDbDate() + Common.getDbTime()}
        <div class='admin-teams-team-box-group'>
          <Input bind:value={team.timestamp.date} label={i18n.plannedPublicationDate[lang]} placeholder={i18n.dateFormat[lang]}
            error={errors.find(error => error.fieldName === 'timestamp.date')?.message[lang]}
            disabled={['loading', 'saved'].includes(display)}/>

          <Input bind:value={team.timestamp.time} label={i18n.plannedPublicationTime[lang]} placeholder='HH:MM'
            error={errors.find(error => error.fieldName === 'timestamp.time')?.message[lang]}
            disabled={['loading', 'saved'].includes(display)}/>
        </div>
      {/if}

      {#if display === 'loading' || display === 'error'}
        <div class='admin-teams-team-box-message-box'>
          <MessageBox value={messageBox ? messageBox[lang] : undefined}/>
        </div>
      {/if}

      {#if display === 'buttons' || display === 'error'}
        <div class='admin-teams-team-box-buttons'>
          <div class='admin-teams-team-box-buttons-save'>
            <Button value={action === 'add' ? i18n.addTeam[lang] : i18n.saveChanges[lang]}
              onClick={() => { addOrEditTeam(); }} icon='save' color='green'/>
          </div>

          <div class='admin-teams-team-box-buttons-discard'>
            <Button value={action === 'add' ? i18n.discardTeam[lang] : i18n.discardChanges[lang]}
              onClick={() => { displayConfirmModal = true; }} icon='delete' color='red'/>
          </div>
        </div>
      {/if}

      {#if display === 'saved'}
        <div class='admin-teams-team-box-message-box-and-button'>
          <div class='admin-teams-team-box-message-box-and-button-message-box'>
            <MessageBox value={messageBox ? messageBox[lang] : undefined}/>
          </div>

          <div class='admin-teams-team-box-message-box-and-button-button'>
            <Button value={i18n.back[lang]} onClick={back} icon='arrow_back'
              cssClass='admin-teams-team-box-message-box-and-button-button-component'/>
          </div>
        </div>
      {/if}
    </div>

    <div class='admin-teams-team-right-panel'>
      <div class='admin-teams-team-right-panel-box'/>

      <div class='admin-teams-team-right-panel-save-without-validation'>
        <Button value={(action === 'add' ? i18n.addTeam[lang] : i18n.saveChanges[lang]) + ' ' + i18n.withoutValidation[lang]}
          onClick={() => { addOrEditTeam(false); }} icon='warning'/>
      </div>
    </div>
  {/if}
</div>

{#if !loading && !error}
  <ConfirmModal bind:display={displayConfirmModal} message={confirmModalMessage[lang]} onConfirm={back} bind:lang/>
{/if}

<Footer bind:lang/>

<style>
  .admin-teams-team {
    display: flex;
    justify-content: space-between;
    gap: 40px;

    max-width: 1440px;

    padding: 40px 60px;

    margin: auto;
  }

  .admin-teams-team-box {
    display: flex;
    flex-direction: column;
    gap: 40px;

    width: calc((100% - 40px) / 3 * 2);
  }

  .admin-teams-team-box-group {
    display: flex;
    gap: 40px;
  }

  .admin-teams-team-box-buttons {
    display: flex;
    gap: 40px;
  }

  .admin-teams-team-box-buttons-save, .admin-teams-team-box-buttons-discard {
    width: 100%;
  }

  .admin-teams-team-box-message-box-and-button {
    display: flex;
    gap: 40px;
  }

  .admin-teams-team-box-message-box-and-button-message-box {
    width: calc((100% - 40px) / 3 * 2);
  }

  .admin-teams-team-box-message-box-and-button-button {
    width: calc((100% - 40px) / 3);
  }

  .admin-teams-team-right-panel {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-shrink: 0;
    gap: 40px;

    width: calc((100% - 40px) / 3);
  }

  .admin-teams-team-right-panel-box {
    height: 100%;

    padding: 20px;

    border-radius: 5px;

    background: var(--grey-1);
  }

  :global(.admin-teams-team-box-message-box-and-button-button-component) {
    height: 100% !important;
  }

  :global(body.dark) .admin-teams-team-right-panel-box {
    background: var(--grey-8);
  }

  @media only screen and (max-width: 1300px) {
    .admin-teams-team-box {
      width: 100%;
    }

    .admin-teams-team-right-panel {
      display: none;
    }
  }

  @media only screen and (max-width: 900px) {
    .admin-teams-team {
      padding: 40px;
    }

    .admin-teams-team-box-message-box-and-button {
      flex-direction: column;
      gap: 20px;
    }

    .admin-teams-team-box-message-box-and-button-message-box, .admin-teams-team-box-message-box-and-button-button {
      width: 100%;
    }

    :global(.admin-teams-team-box-message-box-and-button-button-component) {
      height: 40px !important;
    }
  }

  @media only screen and (max-width: 600px) {
    .admin-teams-team-box-group {
      flex-direction: column;
    }

    .admin-teams-team-box-buttons {
      flex-direction: column;
      gap: 20px;
    }
  }

  @media only screen and (max-width: 350px) {
    .admin-teams-team {
      padding: 40px 20px;
    }
  }
</style>
